import { Tag } from "antd";
import { useTranslation } from "react-i18next";
import { BsArrowLeft, BsCaretLeftFill, BsCaretRightFill } from "react-icons/bs";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { PPE_ALERT_TYPE } from "utils/constants";
import PPEGELActionButtons from "./PPEGELActionButtons";
import { usePPEGEL } from "./PPEGELProvider";

const typeTagColor = {
  [PPE_ALERT_TYPE.GELS_SANCTIONS]: "error",
  [PPE_ALERT_TYPE.PPE_NATIONALES]: "warning",
  [PPE_ALERT_TYPE.PPE_INTERNATIONALES]: "warning",
};

const FieldGroup = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="ppegel-detail__field">
      <span className="ppegel-detail__field-label">{label}</span>
      <span className="ppegel-detail__field-value">{value}</span>
    </div>
  );
};

const AlertInfoGEL = ({ details, t }) => {
  const FIELDS = t("DETAIL.FIELDS", { returnObjects: true });
  return (
    <div className="ppegel-detail__alert-fields">
      <FieldGroup
        label={FIELDS.IDENTITY_REF}
        value={details.identity_reference || details.identity}
      />
      <FieldGroup label={FIELDS.RESIDENCE} value={details.residence} />
      <FieldGroup label={FIELDS.DESCRIPTION} value={details.description} />
      <FieldGroup label={FIELDS.GENDER} value={details.gender} />
      <FieldGroup label={FIELDS.TYPE} value={details.type} />
      <FieldGroup label={FIELDS.OTHER_INFO} value={details.other_information} />
    </div>
  );
};

const AlertInfoPPE = ({ details, t }) => {
  const FIELDS = t("DETAIL.FIELDS", { returnObjects: true });
  return (
    <div className="ppegel-detail__alert-fields">
      <FieldGroup label={FIELDS.IDENTITY} value={details.identity} />
      <FieldGroup label={FIELDS.MANDATES} value={details.mandates} />
      <FieldGroup
        label={FIELDS.OCCUPATION}
        value={details.occupation || details.functions}
      />
      <FieldGroup label={FIELDS.BIRTH_DATE} value={details.birth_date} />
      <FieldGroup
        label={FIELDS.NATIONALITY}
        value={details.identity_reference}
      />
      <FieldGroup label={FIELDS.GENDER} value={details.gender} />
      <FieldGroup label={FIELDS.DESCRIPTION} value={details.description} />
      <FieldGroup
        label={FIELDS.POLITICAL_PARTY}
        value={details.political_party}
      />
      <FieldGroup label={FIELDS.ALIAS} value={details.alias_name} />
      <FieldGroup label={FIELDS.TYPE} value={details.type} />
      <FieldGroup label={FIELDS.PARENTS} value={details.parents} />
      <FieldGroup label={FIELDS.SIBLINGS} value={details.siblings} />
      <FieldGroup label={FIELDS.SPOUSE} value={details.spouse} />
      <FieldGroup label={FIELDS.CHILDREN} value={details.children} />
    </div>
  );
};

const AlertDetailView = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PPEGEL);
  const {
    persons,
    selectedPersonIndex,
    selectedAlertIndex,
    selectAlert,
    backToList,
  } = usePPEGEL();

  const selectedPerson = persons[selectedPersonIndex];
  const alerts = selectedPerson?.alerts || [];
  const alert = alerts[selectedAlertIndex];

  if (!alert) return null;

  const isFirst = selectedAlertIndex === 0;
  const isLast = selectedAlertIndex === alerts.length - 1;
  const isGEL = alert.type === PPE_ALERT_TYPE.GELS_SANCTIONS;

  return (
    <div className="ppegel-detail">
      <div className="ppegel-detail__nav">
        <Button
          type="primary--link"
          className="ppegel-detail__back-link"
          onClick={backToList}
        >
          <BsArrowLeft /> {t("DETAIL.BACK")}
        </Button>
        <div className="ppegel-detail__nav-arrows">
          {!isFirst && (
            <Button
              type="primary--link"
              onClick={() => selectAlert(selectedAlertIndex - 1)}
            >
              <BsCaretLeftFill />
              {t("DETAIL.PREV_ALERT")}
            </Button>
          )}
          {!isLast && (
            <Button
              type="primary--link"
              onClick={() => selectAlert(selectedAlertIndex + 1)}
            >
              {t("DETAIL.NEXT_ALERT")} <BsCaretRightFill />
            </Button>
          )}
        </div>
      </div>

      <div className="ppegel-detail__header">
        <h2 className="ppegel-detail__matched-name">{alert.matchedName}</h2>
        <div className="ppegel-detail__tags">
          <Tag color={typeTagColor[alert.type] || "default"}>
            {t(`ALERT_TYPES.${alert.type}`) || alert.type}
          </Tag>
          <Tag color="blue">{alert.score}%</Tag>
          <Tag>{alert.source}</Tag>
        </div>
      </div>

      <PPEGELActionButtons alert={alert} personId={selectedPerson?.personId} />

      <div className="ppegel-detail__content">
        {isGEL ? (
          <AlertInfoGEL details={alert.details || {}} t={t} />
        ) : (
          <AlertInfoPPE details={alert.details || {}} t={t} />
        )}
      </div>
    </div>
  );
};

export default AlertDetailView;
